import React, { useCallback, useMemo } from "react"
import { Routes, Route } from "react-router-dom"
import { AnimatePresence } from "framer-motion"

import _ from "lodash"

import config from "config"

import { useVenue } from "providers/venue"
import { useAppUI } from "providers/ui"
import { ParkingFinderContainer } from "containers/ParkingFinderContainer"
import { useSponsoredPopupBanner } from "hooks/sponsored-content/useSponsoredPopupBanner"

// Extensions
import {
  getAllExtensionRoutes,
  getExtensionAppComponentConfig,
  getExtensionAppLayoutConfig,
  getExtensionThemeFactories,
  mergeRoutes,
  registerRouteByConfig,
} from "utils/extension"
import { extensionConfigs } from "extensions"

// Theme
import {
  generateTheme,
  appThemeOptionFactory as directoryThemeOptionFactory,
} from "theme"
import { ThemeProvider } from "providers/mui-extended/ThemeProvider"

// Routes
import { homeNavigations, standardRoutes } from "standard"

// Pages
import { HomePage } from "pages/home"
import { Layout as MapLayout } from "pages/maps/_layout"

// Components, Layouts
import { AppLayout } from "layouts/AppLayout"
import { ScrollToTop } from "components/ScrollToTop"
import { LoadingScreen, MapUpdatingScreen } from "components/LoadingScreen"
import { MotionDiv, showContent } from "components/motions"
import { AdImageCarousel, AdModal } from "components/domains/ads"
import { DEVICE_MOBILE } from "constant"
import { createIndexRoute } from "providers/route/utils/createIndexRoute"
import { MapIndex } from "pages/maps"
import { createTheme } from "@mui/material"

// Extension routes
const extensionRoutes = getAllExtensionRoutes(
  config("venueExtension"),
  extensionConfigs,
  DEVICE_MOBILE
)

// Merge Standard + Extension routes
const { pageRoutes, mapRoutes, baseRoutes } = mergeRoutes(
  _.get(standardRoutes, DEVICE_MOBILE),
  extensionRoutes
)

// Extension Themes
const extensionThemeConfig = getExtensionThemeFactories(
  config("venueExtension"),
  extensionConfigs
)

// Extension App Layout Config
const { search } = getExtensionAppLayoutConfig(
  config("venueExtension"),
  extensionConfigs,
  DEVICE_MOBILE
)

// Extension App Components Config
const appComponentConfig = getExtensionAppComponentConfig(
  config("venueExtension"),
  extensionConfigs,
  DEVICE_MOBILE
)
const { loadingScreen, preservedView, parkingModal } = appComponentConfig

// App Theme Factory
const appThemeOptionFactory = _.get(directoryThemeOptionFactory, DEVICE_MOBILE)

export const StandardApp = () => {
  // Generate a venue theme
  const { themeOptions: venueThemeOptions, dataLoaded, indexPath } = useVenue()
  const { mapReady, mapLoading, themeDirection } = useAppUI()
  const { popupBanners, navigateToSponsoredLink, popupOpen, closePopup } =
    useSponsoredPopupBanner()
  const theme = generateTheme(venueThemeOptions, appThemeOptionFactory, {
    extendThemeFactories: extensionThemeConfig.themeFactories,
    ignoreAppThemeFactory: extensionThemeConfig.ignoreAppThemeFactory,
    ignoreDeviceThemeFactory: extensionThemeConfig.ignoreDeviceThemeFactory,
  })

  const isAppReady = useMemo(
    () => mapReady && dataLoaded,
    [mapReady, dataLoaded]
  )

  const onClickSlide = useCallback(
    (_splide, { slide }, _event) => {
      navigateToSponsoredLink(slide.getAttribute("link"))
      closePopup()
    },
    [navigateToSponsoredLink, closePopup]
  )

  const themeWithDirection = useMemo(
    () =>
      createTheme(theme, {
        direction: themeDirection,
        isRtl: themeDirection === "rtl",
      }),
    [theme, themeDirection]
  )

  return (
    <ThemeProvider theme={themeWithDirection}>
      <ScrollToTop />
      {isAppReady ? preservedView : null}
      <AnimatePresence mode="sync">
        {!isAppReady
          ? loadingScreen || <LoadingScreen key="loading-screen" />
          : null}
        <MapUpdatingScreen key="map-updating" open={mapLoading} />
        <MotionDiv
          key="content"
          variants={showContent}
          initial="initial"
          animate="animate"
        >
          <Routes>
            <Route path="/" element={<AppLayout search={search} />}>
              {createIndexRoute({ to: indexPath })}
              {baseRoutes.map(registerRouteByConfig)}
              <Route
                index
                element={<HomePage navigations={homeNavigations} />}
              />
              <Route path="/*">
                {/* Add or override specific route extension pages here. */}
                {pageRoutes.map(registerRouteByConfig)}
              </Route>
              <Route path="/maps/*" element={<MapLayout />}>
                <Route index element={<MapIndex />} />
                {/* The indoorPage parent router treats the route child parameter as the same parameter as its own. */}
                {mapRoutes.map(registerRouteByConfig)}
              </Route>
            </Route>
          </Routes>
        </MotionDiv>
      </AnimatePresence>
      <AdModal open={popupOpen} onClose={closePopup}>
        <AdImageCarousel data={popupBanners} onClickSlide={onClickSlide} />
      </AdModal>
      <ParkingFinderContainer renderModal={parkingModal} />
    </ThemeProvider>
  )
}
